/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { LanguageService } from '@core/i18n/language.service';
import { handleRequest } from '@shared/utils/http-utils';
import { environment } from '@env/environment';
import { AuthService } from '@core/auth/auth.service';
import { AuthInterceptor } from '@core/http/auth.interceptor';

@Injectable({
  providedIn: 'root',
})
export class ApiSysService {
  private url;
  private gatewayUrl;

  private get language(): string {
    return this.langService.getLanguage();
  }

  constructor(
    private http: HttpClient,
    public langService: LanguageService,
    public authService: AuthService,
  ) {
    this.url = environment.url_general_api;
    this.gatewayUrl = environment.url_gateway_api;
  }

  private get authHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    return token ? new HttpHeaders().set('Authorization', `Bearer ${token}`) : new HttpHeaders();
  }

  private get layoutRequestContext(): HttpContext {
    return new HttpContext().set(AuthInterceptor.SKIP_AUTH_ERROR_REDIRECT, true);
  }

  getAllOrganizations(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/dataroutes/allorganizations`, { headers });
  }

  getOrganizationsOld(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/organizations/${this.language}`, { headers });
  }

  async getOrganizations(token: string): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/organizations/${this.language}`,
      token,
    );
  }

  getOrganizationById(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/organizations/${id}/${this.language}`, { headers });
  }

  getOrganization(token: string, id: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/organizations/${id}/${this.language}`, { headers });
  }

  getPersonnel(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/personnel`, { headers });
  }

  /**
   * Returns only the authenticated user's own personnel record, regardless of user_type.
   * Response: { ok, data: { id, per_user, org_id, org_name, rolenameArray, ... }, message }
   * Use this instead of getPersonnel() whenever you need the current user's own profile.
   */
  getPersonnelSelf(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/personnel/me`, { headers });
  }

  getPersonnelById(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/personnel/${id}`, { headers });
  }

  getAllOrgsSimple(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/allorgs`, {
      headers,
    });
  }

  getRolesOld(token: string, org_id: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/rolesOrg/${this.language}/${org_id}`, { headers });
  }

  async getRoles(token: string, org_id: number): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/rolesOrg/${this.language}/${org_id}`,
      token,
    );
  }

  getRolesForOrganization(org_id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http
      .get(`${this.url}/approutes/rolesOrg/${this.language}/${org_id}`, { headers })
      .pipe(
        catchError(() => {
          return this.http.get(`${this.url}/approutes/categories/${this.language}/${org_id}`, {
            headers,
          });
        }),
      );
  }

  async perRequestInfrastructure(
    token: string,
    str_id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'POST',
      `${this.url}/petitionroutes/PerInfrastructurePetitions`,
      token,
      { str_id },
    );
  }

  async perRequestEquipment(token: string, eqp_id: number): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'POST',
      `${this.url}/petitionroutes/PerResourcePetitions`,
      token,
      { eqp_id },
    );
  }

  requestEquipment(eqp_id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(
      `${this.url}/petitionroutes/PerResourcePetitions`,
      { eqp_id },
      { headers },
    );
  }

  async getPerInfrastructurePetitions(token: string): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/petitionroutes/PerInfrastructurePetitions/${this.language}`,
      token,
    );
  }

  getPersonnelEquipmentPetitions(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/petitionroutes/PerResourcePetitions/${this.language}`, {
      headers,
    });
  }

  addCategory(token: string, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.post(`${this.url}/approutes/categories/add`, value, {
      headers,
    });
  }

  removeCategory(token: string, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.request('delete', `${this.url}/approutes/categories/remove`, {
      headers,
      body: value,
    });
  }

  async getInfrastructures(token: string): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/infrastructures/lang/${this.language}`,
      token,
      undefined,
      undefined,
      this.layoutRequestContext,
    );
  }

  validateOrganizationCategory(
    token: string,
    categoryId: number,
    accept_value: number,
  ): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(
      `${this.url}/petitionroutes/PendingCategories/${categoryId}`,
      { accept_value },
      { headers },
    );
  }

  fetchInfrastructures(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/infrastructures/lang/${this.language}`, {
      headers,
    });
  }

  fetchInfrastructuresMap(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/infrastructuresmap/${this.language}`, { headers });
  }

  fetchOrganizations(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/organizations/${this.language}`, { headers });
  }

  deleteOrganization(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/organizations/${id}/truedelete`, {
      headers,
    });
  }

  createOrganization(data: any): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/organizations/${this.language}`, data, {
      headers,
    });
  }

  getInfrastructureById(id: string): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/infrastructures/${id}`, { headers });
  }

  resolveInfrastructureId(identifier: string): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/infrastructures/resolve/${identifier}`, {
      headers,
    });
  }

  async getInfrastructureByCode(token: string, code: string): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/infrastructures/getbycode/${code}`,
      token,
    );
  }

  async getInfrastructuresByUserId(
    token: string,
    id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/infrastructures/pers`, token, {
      perId: id,
      lang: this.language,
    });
  }

  fetchInfrastructuresByUserId(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/infrastructures/pers/${this.language}/${id}`, {
      headers,
    });
  }

  async getInfrastructuresByOrgId(token: string, id: number): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/infrastructures/org/${id}/${this.language}`,
      token,
    );
  }

  fetchInfrastructuresByOrgId(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/infrastructures/org/${id}/${this.language}`, {
      headers,
    });
  }

  async getInfrastructureFilterResults(
    token: string,
    infName: string,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/infrastructuresfilter/${infName}`,
      token,
    );
  }

  fetchEquipment(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/equipment`, { headers });
  }

  async getEquipment(token: string): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'GET', `${this.url}/approutes/equipment`, token);
  }

  getAllEquipments(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/equipments/all`, { headers });
  }

  getEquipments(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/equipment`, { headers });
  }

  async equipmentPetitions(equipment: any): Promise<{ data?: any; error?: any }> {
    const token = this.authService.getAccessToken() ?? '';
    return handleRequest(
      this.http,
      'POST',
      `${this.url}/petitionroutes/OrgEquipmentPetitions`,
      token,
      equipment,
    );
  }

  getInfrastructure(token: string, id: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/infrastructures/${id}`, {
      headers,
    });
  }

  getEquipmentbyId(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/equipment/${id}`, { headers });
  }

  async assignInfrastructureToOrg(
    token: string,
    params: {
      str_id: number;
      org_id: number;
      cat_id_array: number[];
      mng_tit_array?: string[] | number[]; // Changed name and type
    },
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'POST',
      `${this.url}/approutes/infrastructures/orgassign`,
      token,
      params,
    );
  }

  async getInfsOfOrg(token: string, id: number): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/infrastructures/orgassign/${id}`,
      token,
    );
  }

  async removeInfrastructureFromOrg(
    token: string,
    org_id: number,
    str_id: number,
    cat_id_array: number[],
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'DELETE',
      `${this.url}/approutes/infrastructures/orgremove`,
      token,
      { org_id, str_id, cat_id_array },
    );
  }

  removeStrFromOrgCats(
    token: string,
    str_id: number,
    org_id: number,
    cat_id_array: number[],
  ): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.request('delete', `${this.url}/approutes/infrastructures/orgremove`, {
      headers,
      body: { org_id, str_id, cat_id_array },
    });
  }

  async assignInfrastructureToUser(
    token: string,
    user_id: number,
    str_id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'POST',
      `${this.url}/approutes/infrastructures/persassign`,
      token,
      { user_id, str_id },
    );
  }

  assignInfraToUser(user_id: number, str_id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(
      `${this.url}/approutes/infrastructures/persassign`,
      { user_id, str_id },
      { headers },
    );
  }

  async removeInfrastructureFromUser(
    token: string,
    user_id: number,
    str_id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'DELETE',
      `${this.url}/approutes/infrastructures/persremove`,
      token,
      { user_id, str_id },
    );
  }

  removeInfraFromUser(user_id: number, str_id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/infrastructures/persremove`, {
      headers,
      body: { user_id, str_id },
    });
  }

  removeStrFromUser(token: string, str_id: number, user_id: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.delete(`${this.url}/approutes/infrastructures/persremove`, {
      headers,
      body: { user_id, str_id },
    });
  }

  async assignEquipmentToOrg(
    token: string,
    eqp_id: number,
    org_id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/equipment/orgassign`, token, {
      eqp_id,
      org_id,
    });
  }

  async removeEquipmentFromOrg(
    token: string,
    eqp_id: number,
    org_id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'DELETE', `${this.url}/approutes/equipment/orgremove`, token, {
      eqp_id,
      org_id,
    });
  }

  async assignEquipmentToUser(
    token: string,
    eqp_id: number,
    user_id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/equipment/persassign`, token, {
      eqp_id,
      user_id,
    });
  }

  assignAircraftToUser(user_id: number, eqp_id: number): Observable<any> {
    console.log('assignAircraftToUser called with user_id:', user_id, 'and eqp_id:', eqp_id);
    const headers = this.authHeaders;
    return this.http.post(
      `${this.url}/approutes/equipment/persassign`,
      { eqp_id, user_id },
      { headers },
    );
  }

  getAircraftPlain(isUAS: boolean = false): Observable<any> {
    return this.http.get(`${this.url}/dataroutes/aircraftplain/${isUAS}`);
  }

  getAircraftModelData(id: number): Observable<any> {
    return this.http.get(`${this.url}/dataroutes/modeldata/${id}`);
  }

  async removeEquipmentFromUser(
    token: string,
    eqp_id: number,
    user_id: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'DELETE', `${this.url}/approutes/equipment/persremove`, token, {
      eqp_id,
      user_id,
    });
  }

  removeAircraftFromUser(user_id: number, eqp_id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/equipment/persremove`, {
      headers,
      body: { eqp_id, user_id },
    });
  }

  async getEquipmentByUserId(token: string, id: number): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/equipment/pers`, token, {
      perId: id,
    });
  }

  fetchEquipmentByUserId(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/equipment/pers/${id}`, { headers });
  }

  async getEquipmentByOrgId(token: string, id: number): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/equipment/org/${id}`, token);
  }

  fetchEquipmentByOrgId(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/equipment/org/${id}`, {}, { headers });
  }

  getEquipmentByOrganizationId(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/equipment/org/${id}`, {}, { headers });
  }

  async postOrganization(token: string, orgData: any): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/organizations`, token, orgData);
  }

  async postPersonnel(token: string, personnelData: any): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'POST',
      `${this.url}/approutes/personnel`,
      token,
      personnelData,
    );
  }

  createPersonnel(payload: any): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/personnel`, payload, { headers });
  }

  async postInfrastructure(
    token: string,
    value: any,
    lang: string = 'eng',
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'POST',
      `${this.url}/approutes/infrastructures/lang/${lang}`,
      token,
      value,
    );
  }

  createEquipment(payload: any): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/equipment`, payload, { headers });
  }

  postSignUp(value: any): Observable<any> {
    return this.http.post(`${this.url}/approutes/signup`, value);
  }

  deleteInfrastructure(token: string, id: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.delete(`${this.url}/approutes/infrastructures/${id}/truedelete`, {
      headers,
    });
  }

  deletePersonnel(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/personnel/${id}/truedelete`, {
      headers,
    });
  }

  deleteEquipment(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/equipment/${id}/truedelete`, {
      headers,
    });
  }

  disableInfrastructure(token: string, id: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(`${this.url}/approutes/infrastructures/${id}/disable`, {}, { headers });
  }

  deleteEquipmentFromOrg(token: string, eqp_id: number, org_id: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.request('delete', `${this.url}/approutes/equipment/orgremove`, {
      headers,
      body: { eqp_id, org_id },
    });
  }

  deleteEquipmentFromUser(token: string, eqp_id: number, user_id: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.delete(`${this.url}/approutes/equipment/persremove`, {
      headers,
      body: { eqp_id, user_id },
    });
  }

  disableEquipment(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(`${this.url}/approutes/equipment/${id}/disable`, {}, { headers });
  }

  patchInfrastructure(token: string, id: string, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(`${this.url}/approutes/infrastructures/${id}`, value, { headers });
  }

  updateEquipmentById(id: number, payload: any): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(`${this.url}/approutes/equipment/${id}`, payload, { headers });
  }

  patchEquipmentOld(token: string, id: number, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(`${this.url}/approutes/equipment/${id}`, value, {
      headers,
    });
  }

  modifyPendingInfrastructures(id: number, accept_value: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(
      `${this.url}/petitionroutes/modifypendinginfs/${id}`,
      { accept_value },
      { headers },
    );
  }

  modifyPendingEquipments(id: number, accept_value: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(
      `${this.url}/petitionroutes/modifypendingeqps/${id}`,
      { accept_value },
      { headers },
    );
  }

  patchOrganization(token: string, id: number, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(`${this.url}/approutes/organizations/${id}/${this.language}`, value, {
      headers,
    });
  }

  patchPersonnel(token: string, id: number, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(`${this.url}/approutes/personnel/${id}`, value, {
      headers,
    });
  }

  forceLogoutPersonnel(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.gatewayUrl}/approutes/auth/logout/${id}`, {}, { headers });
  }

  validateOrganization(token: string, orgId: number, correlationId?: string): Observable<any> {
    let headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    if (correlationId) {
      headers = headers.set('X-Correlation-ID', correlationId);
    }
    return this.http.patch(`${this.url}/approutes/validateOrg/${orgId}`, {}, { headers });
  }

  getAddressSimilarity(token: string, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(
      `${this.url}/approutes/addresses/similarity/${encodeURIComponent(value)}`,
      { headers },
    );
  }

  getAddressById(token: string, id: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/address/${id}`, { headers });
  }

  createAddress(token: string, value: any): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.post(`${this.url}/approutes/address`, value, { headers });
  }

  acceptCategory(
    token: string,
    org_id: number,
    cat_id: number,
    sub_cat_id: number,
  ): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.post(
      `${this.url}/approutes/categories/accept`,
      { org_id, cat_id, sub_cat_id },
      { headers },
    );
  }

  rejectCategory(
    token: string,
    org_id: number,
    cat_id: number,
    sub_cat_id: number,
  ): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.delete(`${this.url}/approutes/categories/reject`, {
      headers,
      body: { org_id, cat_id, sub_cat_id },
    });
  }

  getOrgEquipmentPetitions(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/OrgEquipmentPetitions/${this.language}`, {
      headers,
    });
  }

  getInfassignedOrg(token: string, id: number, code: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/infrastructures/orgassign/${id}/${code}`, {
      headers,
    });
  }

  getNetworks(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/networks`, { headers });
  }

  async getNetworksWithStrs(token: string, orgId: number): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'GET',
      `${this.url}/approutes/networks/orgIns/:${orgId}`,
      token,
    );
  }

  deleteNetwork(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/networks/${id}/${this.language}`, { headers });
  }

  createNetwork(networkData: {
    name: string;
    short_name?: string;
    infrastructure_ids?: number[];
  }): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/networks`, networkData, { headers });
  }

  getNetworkById(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/networks/${id}/${this.language}`, { headers });
  }

  async getNetworksUser(token: string): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'GET', `${this.url}/approutes/network/allnetworks`, token);
  }

  updateNetwork(
    networkId: number,
    updateData: { net_name?: string; short_name?: string },
  ): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(
      `${this.url}/approutes/networks/${networkId}/${this.language}`,
      updateData,
      { headers },
    );
  }

  async rmInfraFromNetwork(
    token: string,
    infraId: number,
    netId: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(
      this.http,
      'DELETE',
      `${this.url}/approutes/networks/infrastructure/${netId}`,
      token,
      { str_id: infraId },
    );
  }

  async addInfraToNetwork(
    token: string,
    infraId: number,
    netId: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/networks/${netId}`, token, {
      str_id: infraId,
    });
  }

  addInfrastructureToNetwork(networkId: number, infrastructureId: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(
      `${this.url}/approutes/networks/${networkId}`,
      { str_id: infrastructureId },
      { headers },
    );
  }

  removeInfrastructureFromNetwork(networkId: number, infrastructureId: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/networks/infrastructure/${networkId}`, {
      headers,
      body: { str_id: infrastructureId },
    });
  }

  getFleets(token: string): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'GET', `${this.url}/approutes/fleets`, token);
  }

  getAllFleets(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/fleets`, { headers });
  }

  createFleet(fleetData: {
    nf_name: string;
    short_name?: string;
    equipment_ids?: number[];
  }): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/fleets`, fleetData, { headers });
  }

  deleteFleet(fleetId: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/fleets/${fleetId}`, { headers });
  }

  getFleetById(id: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/fleets/${id}`, { headers });
  }

  addAircraftToFleet(fleetId: number, aircraftId: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(
      `${this.url}/approutes/fleets/${fleetId}`,
      { eqp_id: aircraftId },
      { headers },
    );
  }

  removeAircraftFromFleet(fleetId: number, aircraftId: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/approutes/fleets/equipment/${fleetId}`, {
      headers,
      body: { eqp_id: aircraftId },
    });
  }

  updateFleet(
    fleetId: number,
    updateData: { nf_name?: string; short_name?: string },
  ): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(`${this.url}/approutes/fleets/${fleetId}`, updateData, { headers });
  }

  getPendingPersonnel(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/PendingPersonnel/${this.language}`, {
      headers,
    });
  }

  getPendingOrganizations(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/PendingOrganizations/${this.language}`, {
      headers,
    });
  }

  getPendingCategories(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/PendingCategories/${this.language}`, {
      headers,
    });
  }

  getPendingInfrastructures(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/pendingInfrastrucutres/${this.language}`, {
      headers,
    });
  }

  getPendingEquipments(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/pendingEquipments/${this.language}`, {
      headers,
    });
  }

  getOrgInfrastructurePetitions(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/OrgInfrastructurePetitions/${this.language}`, {
      headers,
    });
  }

  getPerInfrastructurePetitionsSync(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/PerInfrastructurePetitions/${this.language}`, {
      headers,
    });
  }

  modifyPerInfrastructurePetitions(
    token: string,
    id: number,
    accept_value: number,
  ): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(
      `${this.url}/petitionroutes/PerInfrastructurePetitions/${id}`,
      { accept_value },
      { headers },
    );
  }

  getPerResourcePetitions(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/PerResourcePetitions/${this.language}`, {
      headers,
    });
  }

  modifyPerResourcePetitions(token: string, id: number, accept_value: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(
      `${this.url}/petitionroutes/PerResourcePetitions/${id}`,
      { accept_value },
      { headers },
    );
  }

  modifyOrgResourcePetitions(token: string, id: number, accept_value: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(
      `${this.url}/petitionroutes/OrgEquipmentPetitions/${id}`,
      { accept_value },
      { headers },
    );
  }

  modifyPersonnel(token: string, id: number, accept_value: number): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.patch(
      `${this.url}/approutes/personnel/${id}`,
      { per_status: accept_value },
      { headers },
    );
  }

  changePassword(data: { currentPassword: string; newPassword: string }): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/change-password`, data, { headers });
  }

  adminChangePassword(data: { userId: number; newPassword: string }): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/approutes/change-password`, data, { headers });
  }

  getPersonnelPreferences(id: string): Observable<any> {
    return this.http.get(`${this.url}/approutes/preferences/${id}`, {
      headers: this.authHeaders,
      context: this.layoutRequestContext,
    });
  }

  updatePersonnelPreferences(id: string, preferences: any): Observable<any> {
    return this.http.patch(`${this.url}/approutes/preferences/${id}`, preferences, {
      headers: this.authHeaders,
    });
  }

  getPersonnelByOrgId(orgId: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/personnel/org/${orgId}`, { headers });
  }

  countAllUserPetitions(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/petitionroutes/countAllUserPetitions`, {
      headers,
      context: this.layoutRequestContext,
    });
  }

  async assignPersonnelToInfrastructure(
    token: string,
    code: string,
    personnelAssignId: number | null,
    gest_titular: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/contact/${code}`, token, {
      personnelAssignId,
      gest_titular,
    });
  }

  async removePersonnelFromInfrastructure(
    token: string,
    code: string,
    gest_titular: number,
  ): Promise<{ data?: any; error?: any }> {
    return handleRequest(this.http, 'POST', `${this.url}/approutes/contact/${code}`, token, {
      personnelAssignId: null,
      gest_titular,
    });
  }

  getInfrastructureLocation(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(
      `${this.url}/approutes/infrastructuresall/locations/${this.langService.getLanguage()}`,
      { headers },
    );
  }
  getInfrastructureCategory(code: string, lang: string, token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.url}/approutes/infrastructuresCategory/${code}/${lang}`, {
      headers,
    });
  }

  modifyOrgInfrastructurePetitions(data: any): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/petitionroutes/OrgInfrastructurePetitions`, data, {
      headers,
    });
  }

  patchOrgInfrastructurePetitions(id: number, accept_value: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(
      `${this.url}/petitionroutes/OrgInfrastructurePetitions/${id}`,
      { accept_value },
      { headers },
    );
  }

  // Issues
  getIssues(): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/petitionRoutes/notifications`, { headers });
  }

  getIssueById(issueId: string): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/petitionRoutes/notifications/${issueId}`, { headers });
  }

  createIssue(issueData: any): Observable<any> {
    const headers = this.authHeaders;
    return this.http.post(`${this.url}/petitionRoutes/notifications`, issueData, { headers });
  }

  deleteIssue(issueId: string): Observable<any> {
    const headers = this.authHeaders;
    return this.http.delete(`${this.url}/petitionRoutes/notifications/${issueId}`, { headers });
  }

  assignFavoriteRadio(code: string, radioId: number): Observable<any> {
    const headers = this.authHeaders;
    return this.http.patch(`${this.url}/approutes/favoriteradio`, { code, radioId }, { headers });
  }

  getFavoriteRadio(code: string): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/favoriteradio/${code}`, { headers });
  }

  getEmailsDocument(token: string, code: string, email: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    const encodedCode = encodeURIComponent(code);
    const encodedEmail = encodeURIComponent(email);
    return this.http.get(
      `${this.url}/approutes/personnel/similarity/${encodedCode}/${encodedEmail}`,
      { headers },
    );
  }

  getSimilarAircrafts(eqp_id: number | string): Observable<any> {
    const headers = this.authHeaders;
    return this.http.get(`${this.url}/approutes/equipment/similarity/${eqp_id}`, { headers });
  }

  getInfrastructureSubCategoryData(): Observable<any> {
    const selectedLanguage = this.langService.getLanguage() || 'eng';
    return this.http.get(`${this.url}/dataroutes/infsubcategories/${selectedLanguage}`);
  }
}
